import * as THREE from 'three';
import { createPartsEditor } from '../../ui/partsEditor.js';
import { classifyMaterial } from '../../ui/materialCategoryRules.js';
import { appendOcLog } from '../../pipeline/ocLog.js';
import type { ViewerTool } from './tool.js';
import type { ViewerContext } from '../context.js';

export const tunerTool: ViewerTool = {
  id: 'tuner',
  enabled: (ctx) =>
    ctx.params.get('pair') !== '1' &&
    (import.meta.env.DEV || ctx.params.get('tuner') === '1') &&
    ctx.rolesOn &&
    !!ctx.paintLinkState,
  async setup(ctx: ViewerContext): Promise<void> {
    const { createMaterialCategoryPanel } = await import('../../ui/materialCategoryPanel.js');
    const model = ctx.config.saveModel ?? ctx.config.id;
    const linkState = ctx.paintLinkState!;
    // 액세서리 그룹은 roots 밖(GlbSource 없음)이라 카테고리 스캔에서 빠짐 → extra 루트로 추가해
    // silveraccessery 등 +++ role 카테고리가 Parts 목록에 뜨게 한다.
    const accGroup = ctx.scene.getObjectByName('accessories');
    const panel = createMaterialCategoryPanel({
      roots: ctx.roots,
      ...(accGroup ? { extraScanRoots: [accGroup] } : {}),
      scene: ctx.scene,
      renderer: ctx.renderer,
      hemiLight: ctx.hemiLight,
      hdriKeys: ctx.env.hdriKeys,
      getEnvMap: () => ctx.env.getEnvMap(),
      onHdrChange: (keyOrPath, forceType) => ctx.env.setHdr(keyOrPath, forceType),
      onInvalidate: ctx.invalidate,
      setCameraLimits: (on: boolean) => ctx.cameraLimits.set(on),
      cameraLimitsInitial: ctx.params.get('limits') === '1',
      currentHdrPreset: ctx.env.currentPreset,
      ...(ctx.config.palette ? { palette: ctx.config.palette } : {}),
      linkState,
      onPaintLinkSelect: (hex: string) => ctx.events.emit('paint:select', hex),
      saveModel: model,
      bakedGlb: ctx.bakedGlb,
      onSaved: (summary: string) => void appendOcLog(model, 'S3', `PBR 튜닝: ${summary}`),
    });
    // paint 색 바뀌면 활성 카테고리 슬라이더 갱신 (옛 refreshCategoryRef 대체)
    ctx.events.on('paint:change', () => panel.refreshActiveCategory());

    // 카테고리 에디터 — 메시 선택 → 우클릭 → New category 로 Tuner 목록에 독립 카테고리 추가.
    createPartsEditor({
      scene: ctx.scene,
      camera: ctx.camera,
      domElement: ctx.renderer.domElement,
      // 피킹 대상은 최상위 루트(차체+액세서리). ctx.root(차체)는 HUD/pairing 이 쓰므로 유지.
      root: ctx.scene.getObjectByName(ctx.config.rootName) ?? ctx.root,
      invalidate: ctx.invalidate,
      addCategory: (cat, meshes, mats) => panel.addCategory(cat, meshes, mats),
      categoryOf: (mesh) => {
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        const named = mats.find((m): m is THREE.MeshStandardMaterial => m instanceof THREE.MeshStandardMaterial && !!m.name);
        return named ? classifyMaterial(named.name, 'O') : null;
      },
      listCategories: () => panel.listCategories(),
      reassign: (cat, meshes) => panel.reassign(cat, meshes),
    });
  },
};
